import {
    Icon,
    Grid,
    Typography,
    Link,
    useTheme,
    RangeBlocks,
    useAppDispatch,
    PargodaTagsEditor,
    IconButton,
    Tooltip,
} from '../../../base';
import {
    ProgressBarWrapper,
    CardWrapper,
    ContactInfoGrid,
    IconButtonsBox,
    StyledIconButton,
    RangeBarWrapper,
} from './PersonalInfoCard.styles';
import {
    createScoreRangeConfig,
    IJobApplication,
    overallScoreLabels,
    scaleScores,
    convertIsoDateToDDMMYYYY,
    EMPTY_VALUE,
    isFirstStepInProcess,
    JobApplicationStatuses,
} from '../../../common';
import { jobApplicationActions } from '../../../actions';
import ShareJobApplicationModal from '../ShareJobApplicationModal';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import TextWithReadMore from '../../TextWithReadMore';
import EditCandidateInfoModal from './EditCandidateInfoModal';
import PargodaModal from '../../PargodaModal';
import DocumentViewer from '../../DocumentViewer';

interface Props {
    jobApplication: IJobApplication;
}

const PersonalInfoCard = (props: Props) => {
    const { jobApplication } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const candidateScaledScore = scaleScores(jobApplication.candidateScore);
    const scoreRangeConfig = createScoreRangeConfig(overallScoreLabels);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [resumeModalOpen, setResumeModalOpen] = useState(false);

    const _isFirstStepInProcess = isFirstStepInProcess(jobApplication);

    const onSaveCandidateClicked = () => {
        dispatch(
            jobApplicationActions.saveForLater({
                id: jobApplication.id,
                isSaved: jobApplication.isSaved,
            })
        );
    };

    const isTransferableApplication =
        jobApplication.status !== JobApplicationStatuses.hired;

    const onTransferCandidateClicked = () => {
        dispatch(
            jobApplicationActions.setTransferCandidateModalData({
                applicationId: jobApplication.id,
                candidateName: `${jobApplication.firstName} ${jobApplication.lastName}`,
            })
        );
        dispatch(jobApplicationActions.setIsTransferCandidateModalOpen(true));
    };

    const headerIconButtonSx = {
        color: theme.palette.grey[600],
        height: 16,
        width: 16,
        marginTop: 0,
        padding: 0,
    };

    const referrerName = jobApplication.referrer
        ? `${jobApplication.referrer.user.firstName} ${jobApplication.referrer.user.lastName}`
        : undefined;

    const contactEmail = jobApplication.referrer?.isPointOfContact
        ? jobApplication.referrer.user.email
        : jobApplication.email;

    const contactPhone = jobApplication.referrer?.isPointOfContact
        ? jobApplication.referrer.user.phone
        : jobApplication.phone;

    return (
        <CardWrapper>
            <Grid container rowSpacing={1}>
                <Grid item xs={12}>
                    <Grid
                        container
                        gap={1}
                        alignItems="flex-start"
                        justifyContent="space-between"
                    >
                        <Grid item display="flex" gap={1} alignItems="baseline">
                            <Grid item display="flex">
                                <Typography variant="h3" gutterBottom>
                                    {`${jobApplication.firstName} ${jobApplication.lastName}`}
                                </Typography>
                            </Grid>
                            <Grid item display={'flex'} gap={1}>
                                {referrerName && (
                                    <>
                                        <Typography
                                            variant="body2"
                                            gutterBottom
                                        >
                                            |
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            gutterBottom
                                        >
                                            {`${t('jobApplicationPage.uploadedBy')} ${referrerName}`}
                                        </Typography>
                                    </>
                                )}
                            </Grid>
                        </Grid>
                        <Grid item display="flex" alignItems="center" gap={1}>
                            <Tooltip
                                title={t(
                                    'jobApplicationsTable.transferCandidate'
                                )}
                            >
                                <IconButton
                                    onClick={onTransferCandidateClicked}
                                    disabled={!isTransferableApplication}
                                    sx={headerIconButtonSx}
                                >
                                    <Icon name="SwapHorizIcon" />
                                </IconButton>
                            </Tooltip>
                            <IconButton
                                onClick={() => setEditModalOpen(true)}
                                disabled={jobApplication.isLegacy}
                                sx={headerIconButtonSx}
                            >
                                <Icon name="EditIcon" />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>
                <ProgressBarWrapper
                    item
                    xs={12}
                    justifyContent={'space-between'}
                >
                    <RangeBarWrapper>
                        <RangeBlocks
                            externalValue={{
                                from: candidateScaledScore > 0 ? 1 : 0,
                                to: candidateScaledScore,
                            }}
                            disabled
                            showSelectionText={false}
                            fillColor={theme.palette.primary.main}
                        />
                    </RangeBarWrapper>
                    <Typography variant="subtitle1" color="text.primary">
                        {scoreRangeConfig.getLabel(
                            candidateScaledScore,
                            candidateScaledScore
                        )}
                    </Typography>
                </ProgressBarWrapper>
                <Grid item xs={12}>
                    <Grid container>
                        <Grid item width="calc(100% - 45px)">
                            <Grid container width="100%">
                                {referrerName && (
                                    <ContactInfoGrid
                                        item
                                        xs={12}
                                        overflow="hidden"
                                    >
                                        <Icon
                                            name={'ContactCalendarIcon'}
                                            size={20}
                                            sx={{
                                                color: theme.palette.grey[600],
                                            }}
                                        />
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                            display="flex"
                                            gap={1}
                                        >
                                            {referrerName}
                                            {jobApplication.referrer
                                                ?.companyName && (
                                                <>
                                                    <Typography variant="body2">
                                                        |
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {`${jobApplication.referrer?.companyName}`}
                                                    </Typography>
                                                </>
                                            )}
                                        </Typography>
                                    </ContactInfoGrid>
                                )}
                                <ContactInfoGrid item xs={12} overflow="hidden">
                                    <Icon
                                        name={'MailIcon'}
                                        size={20}
                                        sx={{ color: theme.palette.grey[600] }}
                                    />
                                    <Link
                                        href={`mailto:${contactEmail}`}
                                        color="inherit"
                                        underline="none"
                                        overflow="inherit"
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {contactEmail}
                                        </Typography>
                                    </Link>
                                </ContactInfoGrid>
                                <ContactInfoGrid item xs={12} overflow="hidden">
                                    <Icon
                                        name={'WhatsappIcon'}
                                        size={20}
                                        sx={{ color: theme.palette.grey[600] }}
                                    />
                                    <Link
                                        href={`tel:${contactPhone}`}
                                        color="inherit"
                                        underline="none"
                                        overflow="inherit"
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {contactPhone}
                                        </Typography>
                                    </Link>
                                </ContactInfoGrid>
                                {/*<ContactInfoGrid item xs={12} sx={{ pb: 0 }}>*/}
                                {/*    <Icon name={'LinkedInIcon'} size={20} color={theme.palette.grey[600]} />*/}
                                {/*    <Link*/}
                                {/*        href={`https://linkedin.com/${personalInfoData.linkedin}`}*/}
                                {/*        color="inherit"*/}
                                {/*        underline="none"*/}
                                {/*        target="_blank"*/}
                                {/*    >*/}
                                {/*        <Typography variant="subtitle1">*/}
                                {/*            {personalInfoData.linkedin}*/}
                                {/*        </Typography>*/}
                                {/*    </Link>*/}
                                {/*</ContactInfoGrid>*/}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container wrap="nowrap">
                        <Grid item width="100%">
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography variant="body2">
                                        {`${t('jobApplicationPage.applicationDate')}: ${convertIsoDateToDDMMYYYY(jobApplication.submissionDate)}`}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2">
                                        {`${t('jobApplicationPage.source')}: ${jobApplication.source || EMPTY_VALUE}`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            justifyContent="flex-end"
                            marginInlineStart="5px"
                            alignItems="flex-end"
                            display="flex"
                        >
                            <IconButtonsBox>
                                <StyledIconButton
                                    size="small"
                                    onClick={() => setShareModalOpen(true)}
                                    buttonColor={theme.palette.grey[600]}
                                >
                                    <Icon name={'ShareButtonIcon'} size={40} />
                                </StyledIconButton>
                                <StyledIconButton
                                    size="small"
                                    onClick={onSaveCandidateClicked}
                                    buttonColor={theme.palette.secondary.main}
                                >
                                    <Icon
                                        name={
                                            jobApplication.isSaved
                                                ? 'BookmarkFilledIcon'
                                                : 'BookmarkButtonIcon'
                                        }
                                        size={40}
                                    />
                                </StyledIconButton>
                            </IconButtonsBox>
                        </Grid>
                    </Grid>
                </Grid>
                {jobApplication.notes && (
                    <Grid item xs={12}>
                        <TextWithReadMore
                            title={t('jobApplicationPage.additionalNotesTitle')}
                            text={jobApplication.notes}
                        />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <PargodaTagsEditor
                        externalAppliedTags={jobApplication.tags}
                        objectId={jobApplication.id}
                        modelName="job_application"
                        disabled={jobApplication.isLegacy}
                    />
                </Grid>
                {!_isFirstStepInProcess && (
                    <Grid item xs={12}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => setResumeModalOpen(true)}
                            sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                            }}
                        >
                            {t('jobApplicationPage.viewResume')}
                        </Link>
                    </Grid>
                )}
            </Grid>
            <ShareJobApplicationModal
                isModalOpen={shareModalOpen}
                setIsModalOpen={setShareModalOpen}
                modalTitle={t('jobApplicationPage.shareCandidate')}
                jobApplication={jobApplication}
            />
            <EditCandidateInfoModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                jobApplication={jobApplication}
            />
            <PargodaModal
                isModalOpen={resumeModalOpen}
                setIsModalOpen={setResumeModalOpen}
                modalTitle={t('jobApplicationPage.candidateResume')}
                sx={{
                    width: '80%',
                    maxWidth: '1200px',
                    height: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Grid
                    container
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        mt: 2,
                    }}
                >
                    <Grid
                        item
                        xs={12}
                        sx={{
                            height: '100%',
                            overflow: 'auto',
                        }}
                    >
                        <DocumentViewer url={jobApplication.resumeUrl} />
                    </Grid>
                </Grid>
            </PargodaModal>
        </CardWrapper>
    );
};

export default PersonalInfoCard;
